import { useState } from 'react';
import { useVersionAdoption, useVersions } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { StatusPill } from '@/shared/ui/status-pill';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ErrorState } from '@/components/common';

const WINDOWS = [7, 30, 90] as const;

/** Dotted numeric comparison, ignoring a build suffix - the server's own ordering. */
function compare(a: string, b: string): number {
  const parts = (v: string) => v.split(/[-+]/)[0]!.split('.').map(Number);
  const [x, y] = [parts(a), parts(b)];
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * Which versions machines are running.
 *
 * Each machine counted once, at the version its latest update check reported
 * in the window - not once per check, which would weight the machines left on
 * longest. What to read before making a version mandatory: everything below
 * it stops until it updates.
 */
export function VersionAdoption() {
  const [days, setDays] = useState<number>(30);
  const adoption = useVersionAdoption(days);
  // The newest published stable build, for "behind": the list is newest first.
  const latest = useVersions({ limit: 1, offset: 0, channel: 'stable' });
  const newest = latest.data?.data[0]?.versionNumber;
  const total = adoption.data?.reduce((sum, row) => sum + row.devices, 0) ?? 0;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="text-sm font-semibold">Adoption</h2>
          <p className="text-[12.5px] text-muted-foreground">
            Which versions machines run, from their update checks.
          </p>
        </div>
        <Select
          value={String(days)}
          onValueChange={(value) => {
            setDays(Number(value));
          }}
        >
          <SelectTrigger className="ml-auto w-36" aria-label="Window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((n) => (
              <SelectItem key={n} value={String(n)}>
                Last {n} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {adoption.isError && (
        <ErrorState
          title="Could not load adoption"
          description={adoption.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void adoption.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Version</TableHead>
              <TableHead className="w-28">Channel</TableHead>
              <TableHead className="w-32 text-right">Machines</TableHead>
              <TableHead className="hidden md:table-cell">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adoption.isPending &&
              Array.from({ length: 3 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={4}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {adoption.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                  No machine has checked for updates in the last {days} days.
                </TableCell>
              </TableRow>
            )}

            {adoption.data?.map((row) => {
              const behind = newest !== undefined && compare(row.version, newest) < 0;
              const share = total ? Math.round((row.devices / total) * 100) : 0;
              return (
                <TableRow key={`${row.version}-${row.channel ?? 'none'}`}>
                  <TableCell className="font-medium tabular-nums">
                    {row.version}
                    {behind && (
                      <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">
                        behind {newest}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.channel ? (
                      <StatusPill status={row.channel} minWidth={64} />
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.devices}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-40 overflow-hidden rounded-full bg-muted">
                        <div
                          className={behind ? 'h-full bg-amber-500' : 'h-full bg-primary'}
                          style={{ width: `${String(share)}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">{share}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableCard>
    </section>
  );
}
